import { useMemo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import type { NodeStyle } from "./types";
import { isNodeStyle } from "./node-style";
import { useAppearanceTheme } from "./appearance-context";
import { seedFromId } from "./sketch/seed";
import {
  sketchShapePaths,
  sketchLinePaths,
  type SketchPath,
} from "./sketch/node-paths";
import { SketchSvg } from "./sketch/SketchSvg";

// Vertical room (px) the divider svg reserves for stroke wobble above and
// below the header's bottom edge.
const DIVIDER_WOBBLE = 4;

interface GroupSketch {
  readonly border: SketchPath[];
  readonly divider: SketchPath[];
}

/**
 * Generate the sketchy container border and header divider for a group in
 * hand-drawn mode. Returns undefined in default mode or when layout has not
 * provided explicit dimensions yet (crisp fallback).
 */
function useGroupSketchPaths(
  id: string,
  width: number | undefined,
  height: number | undefined,
  nodeStyle: NodeStyle | undefined,
): GroupSketch | undefined {
  const theme = useAppearanceTheme();
  const stroke = nodeStyle?.borderColor ?? "var(--group-border, #5a5a7a)";
  const strokeWidth =
    typeof nodeStyle?.borderWidth === "number" ? nodeStyle.borderWidth : 1;

  return useMemo(() => {
    if (theme !== "hand-drawn" || width === undefined || height === undefined) {
      return undefined;
    }
    const seed = seedFromId(id);
    return {
      // Container border: stroke-only rectangle (the CSS background stays).
      border: sketchShapePaths({
        shape: "rectangle",
        width,
        height,
        stroke,
        strokeWidth,
        seed,
      }),
      // Header divider, drawn in its own svg anchored to the header bottom.
      divider: sketchLinePaths({
        x1: 0,
        y1: DIVIDER_WOBBLE,
        x2: width,
        y2: DIVIDER_WOBBLE,
        stroke,
        strokeWidth,
        seed,
      }),
    };
  }, [theme, id, width, height, stroke, strokeWidth]);
}

/** Sketchy header divider line, anchored to the bottom edge of the header. */
function SketchDivider({
  width,
  paths,
}: {
  readonly width: number;
  readonly paths: readonly SketchPath[];
}) {
  return (
    <svg
      viewBox={`0 0 ${String(width)} ${String(2 * DIVIDER_WOBBLE)}`}
      data-testid="sketch-divider"
      style={{
        position: "absolute",
        left: 0,
        bottom: -DIVIDER_WOBBLE,
        width: "100%",
        height: 2 * DIVIDER_WOBBLE,
        overflow: "visible",
        pointerEvents: "none",
      }}
    >
      {paths.map((p, i) => (
        <path
          key={i}
          d={p.d}
          stroke={p.stroke}
          strokeWidth={p.strokeWidth}
          fill={p.fill}
        />
      ))}
    </svg>
  );
}

/**
 * Custom group node component for the diagram renderer. Renders a
 * labelled container that acts as a parent for child nodes. React Flow
 * positions children absolutely inside the group; this component
 * provides the visual frame, header label, and hidden connection handles.
 * In hand-drawn mode the border and header divider are drawn as sketchy
 * SVG strokes instead of CSS borders.
 */
export function GroupNode({ id, data, width, height }: NodeProps) {
  const nodeStyle: NodeStyle | undefined = isNodeStyle(data.nodeStyle)
    ? data.nodeStyle
    : undefined;

  const sketch = useGroupSketchPaths(id, width, height, nodeStyle);
  const handDrawn = sketch !== undefined;

  const css: React.CSSProperties = {
    boxSizing: "border-box",
    width: width ?? "100%",
    height: height ?? "100%",
    borderRadius: 6,
    // Longhand border properties only — see buildNodeCss in DefaultNode.tsx.
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "var(--group-border, #5a5a7a)",
    background: "var(--group-bg, rgba(30, 30, 46, 0.5))",
    position: "relative",
    ...nodeStyle,
  };

  if (handDrawn) {
    // The sketchy overlay draws the border; keep the CSS background.
    css.borderStyle = "none";
  }

  const headerCss: React.CSSProperties = {
    padding: "4px 10px",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--group-header-text, #b0b0c0)",
    borderBottom: "1px solid var(--group-border, #5a5a7a)",
    background: "var(--group-header-bg, rgba(40, 40, 56, 0.7))",
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
  };

  if (handDrawn) {
    // The sketchy divider replaces the CSS border; position it relative to
    // the header's real bottom edge so it tracks the content height.
    headerCss.borderBottom = "none";
    headerCss.position = "relative";
  }

  return (
    <div style={css} data-testid="group-container">
      <Handle
        type="target"
        position={Position.Top}
        style={{ visibility: "hidden" }}
      />
      {sketch !== undefined && width !== undefined && height !== undefined && (
        <SketchSvg width={width} height={height} paths={sketch.border} />
      )}
      <div style={headerCss}>
        {typeof data.description === "string" && data.description.length > 0 ? (
          <span className="diagram-label-hint" title={data.description}>
            {typeof data.label === "string" ? data.label : ""}
          </span>
        ) : (
          <span>{typeof data.label === "string" ? data.label : ""}</span>
        )}
        {sketch !== undefined && width !== undefined && (
          <SketchDivider width={width} paths={sketch.divider} />
        )}
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ visibility: "hidden" }}
      />
    </div>
  );
}
